import { BaseError } from 'make-error';
import { Permission, platform } from '../Platform';
import {
    BrightSignHdmiInputHandler,
    BroadsignVideoHandler,
    GenericHandlerConfig,
    HivestackHandlerConfig,
    HivestackVideoHandler,
    HlsMediaSourceStreamHandler,
    HlsNativeStreamHandler,
    PlaceExchangeVideoHandler,
    ReachHandlerConfig,
    ReachVideoHandler,
    RtspNativeStreamHandler,
    SimpleHandlerConfig,
    VengoHandlerConfig,
    VengoVideoHandler,
    VimeoHandler,
    VimeoHandlerConfig,
    VistarVideoHandler,
    YouTubeHandler,
    YouTubeHandlerConfig,
} from './Video';
import { VideoHandlerConfig } from './Video/Abstract';
import './MediaController.scss';

export { Media, State, type VideoHandlerInterface } from './Video';

type OmitVimeoHandlerConfig = Omit<VimeoHandlerConfig, 'muteBeforeUserInteraction'>;
type OmitYouTubeHandlerConfig = Omit<YouTubeHandlerConfig, 'muteBeforeUserInteraction'>;
type OmitReachHandlerConfig = Omit<ReachHandlerConfig, 'muteBeforeUserInteraction'>;
type OmitSimpleHandlerConfig = Omit<SimpleHandlerConfig, 'muteBeforeUserInteraction'>;
type OmitHivestackHandlerConfig = Omit<HivestackHandlerConfig, 'muteBeforeUserInteraction'>;
type OmitVengoHandlerConfig = Omit<VengoHandlerConfig, 'muteBeforeUserInteraction'>;

type OmitGenericHandlerConfig = Omit<GenericHandlerConfig, 'muteBeforeUserInteraction'>;

function modConfig<T>(config: T): T & Pick<VideoHandlerConfig, 'muteBeforeUserInteraction'> {
    const newConfig = {
        ...config,
        muteBeforeUserInteraction: platform().hasPermission(
            Permission.MUTE_BEFORE_USER_INTERACTION
        ),
    };
    return newConfig;
}

export class MediaNotSupported extends BaseError {
    constructor(message: string) {
        super(message);
    }
}

export function createVimeoHandler(config: OmitVimeoHandlerConfig): VimeoHandler {
    return new VimeoHandler(modConfig(config));
}

export function createYouTubeHandler(config: OmitYouTubeHandlerConfig): YouTubeHandler {
    return new YouTubeHandler(modConfig(config));
}

export function createReachVideoHandler(config: OmitReachHandlerConfig): ReachVideoHandler {
    return new ReachVideoHandler(modConfig(config));
}

export function createPlaceExchangeVideoHandler(
    config: OmitSimpleHandlerConfig
): PlaceExchangeVideoHandler {
    return new PlaceExchangeVideoHandler(modConfig(config));
}

export function createVistarVideoHandler(config: OmitSimpleHandlerConfig): VistarVideoHandler {
    return new VistarVideoHandler(modConfig(config));
}

export function createHivestackVideoHandler(
    config: OmitHivestackHandlerConfig
): HivestackVideoHandler {
    return new HivestackVideoHandler(modConfig(config));
}

export function createBroadsignVideoHandler(
    config: OmitSimpleHandlerConfig
): BroadsignVideoHandler {
    return new BroadsignVideoHandler(modConfig(config));
}

export function createVengoVideoHandler(config: OmitVengoHandlerConfig): VengoVideoHandler {
    return new VengoVideoHandler(modConfig(config));
}

/**
 * @throws {@link MediaNotSupported} if not supported on platform
 * @todo Implement VLC
 */
export function createHdmiInputHandler(
    config: OmitGenericHandlerConfig
): BrightSignHdmiInputHandler {
    if (platform().hasPermission(Permission.BS_HDMI)) {
        return new BrightSignHdmiInputHandler(modConfig(config));
    }

    if (platform().hasPermission(Permission.VLC_HDMI)) {
        throw new ReferenceError('Not Implemented.');
    }

    throw new MediaNotSupported('Video capture not supported on this platform.');
}

/**
 * @throws {@link MediaNotSupported} if not supported on platform
 * @todo Implement VLC
 */
export function createRtpStreamHandler(config: OmitSimpleHandlerConfig): RtspNativeStreamHandler {
    if (platform().hasPermission(Permission.NATIVE_RTP)) {
        return new RtspNativeStreamHandler(modConfig(config));
    }

    if (platform().hasPermission(Permission.VLC_RTP)) {
        throw new ReferenceError('Not Implemented.');
    }

    throw new MediaNotSupported('Cannot play RTSP/RTP video streams on this platform.');
}

export function createHlsStreamHandler(
    config: OmitSimpleHandlerConfig
): HlsMediaSourceStreamHandler | HlsNativeStreamHandler {
    if (platform().hasPermission(Permission.NATIVE_HLS)) {
        return new HlsNativeStreamHandler(modConfig(config));
    }

    return new HlsMediaSourceStreamHandler(modConfig(config));
}
